import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Captions,
  LayoutGrid,
  Loader2,
  Maximize2,
  UserPlus,
  X,
  Mic,
  MicOff,
  Minimize2,
  MonitorUp,
  PhoneOff,
  PinOff,
  Video,
  VideoOff,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import type {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  ILocalVideoTrack,
  IMicrophoneAudioTrack,
} from 'agora-rtc-sdk-ng';

import { cn } from '@/lib/utils';
import { decodeCaption } from '@/lib/agoraCaptions';
import { parseEmails, type CaptionsState, type RtcAccess } from '@/services/meetings.service';

interface MeetingRoomProps {
  rtc: RtcAccess;
  displayName: string;
  title?: string | null;
  /** Nur zusehen: nichts veröffentlichen, nur empfangen. */
  viewOnly?: boolean;
  /** Externe Gäste einladen (nur Agenten) — liefert Anzahl gesendeter Mails. */
  onInvite?: (emails: string[]) => Promise<number>;
  /** Lebenszeichen (alle 30 s, mit Teilnehmerzahl) — für das Auto-Schließen leerer Räume. */
  onHeartbeat?: (count: number) => void;
  /** uid → Anzeigename (Kachel-Labels + Beitritts-/Verlassen-Toasts). */
  peers?: { get: () => Promise<Record<string, string>> };
  /** Beitrittsanfragen (NUR Raum-Besitzer): Anfragen holen + entscheiden. */
  requests?: {
    get: () => Promise<{ id: string; user_id: string; name: string | null }[]>;
    decide: (reqId: string, decision: 'approve' | 'deny') => Promise<void>;
  };
  /** Live-Untertitel steuern (nur Desk-Nutzer — kostet pro Minute).
      Gäste EMPFANGEN Untertitel trotzdem, sie kommen über den RTC-Kanal. */
  captionsControl?: {
    get: () => Promise<CaptionsState>;
    start: (source: string, targets: string[]) => Promise<CaptionsState>;
    stop: () => Promise<CaptionsState>;
  };
  onLeave: () => void;
}

/** Eine Untertitel-Zeile je Sprecher: Transkript + Übersetzungen dazu. */
interface CaptionLine {
  text: string;
  final: boolean;
  trans: Record<string, string>;
  ts: number;
}

type StageId = 'local' | string; // string = remote uid

/** Rasteransicht: Spaltenzahl nach Teilnehmerzahl (Meet-Verhalten). */
const strip_len_cols = (n: number) => (n <= 1 ? 1 : n === 2 ? 2 : n <= 4 ? 2 : n <= 9 ? 3 : 4);

/**
 * Gesprächsraum — Bühne + Filmstreifen, Navy-Palette, hell/dunkel über
 * CSS-Variablen (meet-room-* in index.css), mobil-first responsiv.
 *
 * Bedienung: Kachel antippen = auf die Bühne holen (Fokusmodus). Auf der
 * Bühne: Zoom (Buttons/Doppelklick), Ziehen zum Verschieben bei Zoom,
 * Vollbild. Ein neu startender Bildschirm-Stream holt sich die Bühne
 * automatisch. Medien laufen Browser ↔ Agora.
 */
export default function MeetingRoom({ rtc, displayName, title, viewOnly = false, onInvite, onHeartbeat, peers, requests, captionsControl, onLeave }: MeetingRoomProps) {
  const { t } = useTranslation();

  const clientRef = useRef<IAgoraRTCClient | null>(null);
  const micRef = useRef<IMicrophoneAudioTrack | null>(null);
  const camRef = useRef<ICameraVideoTrack | null>(null);
  const screenRef = useRef<ILocalVideoTrack | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  /** Bühnen- und Streifencontainer je Teilnehmer — play() braucht echte DOM-Knoten. */
  const tileEls = useRef(new Map<StageId, HTMLDivElement>());

  const [joined, setJoined] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(viewOnly);
  const [camOn, setCamOn] = useState(!viewOnly);
  const [hasCam, setHasCam] = useState(!viewOnly);
  const [sharing, setSharing] = useState(false);
  const [remotes, setRemotes] = useState<IAgoraRTCRemoteUser[]>([]);
  /** uid → Anzeigename (Server-Mapping; Fallback #uid). */
  const [peerNames, setPeerNames] = useState<Record<string, string>>({});
  const peerNamesRef = useRef(peerNames);
  useEffect(() => { peerNamesRef.current = peerNames; }, [peerNames]);
  /** Bekleyen Beitrittsanfragen (nur Besitzer bekommt welche). */
  const [joinReqs, setJoinReqs] = useState<{ id: string; user_id: string; name: string | null }[]>([]);
  /** Ansicht: 'speaker' = aktiver Sprecher automatisch groß (Standard),
      'grid' = alle gleichzeitig. Pin überstimmt beides (eine Person fest). */
  const [viewMode, setViewMode] = useState<'speaker' | 'grid'>('speaker');
  const [pinned, setPinned] = useState<StageId | null>(null);
  const [speakerUid, setSpeakerUid] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [invitePanel, setInvitePanel] = useState(false);
  const [inviteText, setInviteText] = useState('');
  const [inviting, setInviting] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; px: number; py: number } | null>(null);

  // ---- Live-Untertitel ------------------------------------------------------
  const [ccPanel, setCcPanel] = useState(false);
  const [ccState, setCcState] = useState<CaptionsState | null>(null);
  const [ccBusy, setCcBusy] = useState(false);
  const [ccVisible, setCcVisible] = useState(true);
  const [srcLang, setSrcLang] = useState('de-DE');
  const [tgtLang, setTgtLang] = useState('none');
  /** Anzeige-Sprache: 'original' oder ein Zielsprachcode. */
  const [displayLang, setDisplayLang] = useState('original');
  const [captionLines, setCaptionLines] = useState<Record<string, CaptionLine>>({});
  /** Es sind je Nachrichten eingetroffen — erst dann Overlay/CC für Gäste. */
  const captionsSeen = Object.keys(captionLines).length > 0;

  // ---- Beitritt & Ereignisse ------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { default: AgoraRTC } = await import('agora-rtc-sdk-ng');
        AgoraRTC.setLogLevel(3);
        const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
        clientRef.current = client;

        const syncRemotes = () => setRemotes([...client.remoteUsers]);
        client.on('user-published', async (user, mediaType) => {
          await client.subscribe(user, mediaType);
          if (mediaType === 'audio') user.audioTrack?.play();
          syncRemotes();
        });
        client.on('user-unpublished', (user, mediaType) => {
          if (mediaType === 'video') setPinned((p) => (p === String(user.uid) ? null : p));
          syncRemotes();
        });
        client.on('user-left', (user) => {
          const id = String(user.uid);
          setPinned((p) => (p === id ? null : p));
          setSpeakerUid((sp) => (sp === id ? null : sp));
          syncRemotes();
          // Google-Meet-Stil: kurzer Hinweis, wer den Raum verlassen hat
          toast(t('shell:meet.leftToast', '{{name}} hat das Meeting verlassen', {
            name: peerNamesRef.current[id] || `#${id.slice(-4)}`,
          }), { duration: 3500 });
        });
        client.on('user-joined', (user) => {
          syncRemotes();
          const id = String(user.uid);
          // Ad tablosunu tazele, sonra bildir (yeni uid çoğu kez tabloda yenidir)
          const announce = (name?: string) => toast(t('shell:meet.joinedToast', '{{name}} ist beigetreten', {
            name: name || peerNamesRef.current[id] || `#${id.slice(-4)}`,
          }), { duration: 3500 });
          if (peers) {
            peers.get().then((map) => { setPeerNames(map); announce(map[id]); }).catch(() => announce());
          } else {
            announce();
          }
        });

        // Aktiver Sprecher: Lautstärke-Indikator (~alle 2 s). Der lauteste
        // Remote-Teilnehmer über der Schwelle wird zum Sprecher — die eigene
        // Stimme zählt nicht (man muss sich nicht selbst groß sehen).
        client.enableAudioVolumeIndicator();
        client.on('volume-indicator', (volumes) => {
          let best: { uid: string; level: number } | null = null;
          for (const v of volumes) {
            const uid = String(v.uid);
            if (uid === String(rtc.uid)) continue;
            if (v.level > 8 && (!best || v.level > best.level)) best = { uid, level: v.level };
          }
          if (best) setSpeakerUid(best.uid);
        });

        // Untertitel des STT-Bots: Protobuf über den Data-Stream. Fremde
        // Data-Stream-Nutzung liefert decodeCaption → null und wird ignoriert.
        client.on('stream-message', (_uid: unknown, payload: Uint8Array) => {
          const m = decodeCaption(payload instanceof Uint8Array ? payload : new Uint8Array(payload as ArrayBuffer));
          if (!m) return;
          setCaptionLines((prev) => {
            const key = String(m.speakerUid);
            const cur = prev[key] || { text: '', final: false, trans: {}, ts: 0 };
            const next: CaptionLine = { ...cur, trans: { ...cur.trans }, ts: Date.now() };
            if (m.dataType === 'translate') {
              for (const tr of m.translations) if (tr.text) next.trans[tr.lang] = tr.text;
            } else {
              next.text = m.text;
              next.final = m.isFinal;
              // Neuer Satz beginnt: alte Übersetzungen gehören zum vorigen.
              if (!cur.final && m.isFinal) { /* Satz abgeschlossen, trans bleibt */ }
              else if (cur.final && !m.isFinal) next.trans = {};
            }
            return { ...prev, [key]: next };
          });
        });

        await client.join(rtc.appId, rtc.channel, rtc.token, rtc.uid);
        if (cancelled) { await client.leave(); return; }

        if (!viewOnly) {
          const mic = await AgoraRTC.createMicrophoneAudioTrack();
          micRef.current = mic;
          const toPublish: (IMicrophoneAudioTrack | ICameraVideoTrack)[] = [mic];
          try {
            const cam = await AgoraRTC.createCameraVideoTrack();
            camRef.current = cam;
            toPublish.push(cam);
          } catch { setHasCam(false); setCamOn(false); }
          await client.publish(toPublish);
        }
        setJoined(true);
      } catch (e: any) {
        setError(
          e?.name === 'NotAllowedError' || /permission/i.test(e?.message || '')
            ? t('shell:meet.micBlocked', 'Mikrofonzugriff wurde blockiert — bitte im Browser erlauben.')
            : e?.message || t('shell:meet.joinFailed', 'Beitritt fehlgeschlagen'),
        );
      }
    })();

    return () => {
      cancelled = true;
      screenRef.current?.close();
      camRef.current?.close();
      micRef.current?.close();
      clientRef.current?.leave().catch(() => {});
      clientRef.current?.removeAllListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtc.channel, rtc.token, rtc.uid, viewOnly]);

  // Peer adları: girişte bir kez çek
  useEffect(() => {
    if (!joined || !peers) return;
    peers.get().then(setPeerNames).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined]);

  // Beitrittsanfragen (nur Besitzer): alle 5 s nachsehen
  useEffect(() => {
    if (!joined || !requests) return;
    const pull = () => requests.get().then(setJoinReqs).catch(() => {});
    pull();
    const id = window.setInterval(pull, 5000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined]);

  const decideJoin = async (reqId: string, decision: 'approve' | 'deny') => {
    if (!requests) return;
    try {
      await requests.decide(reqId, decision);
      setJoinReqs((cur) => cur.filter((r) => r.id !== reqId));
    } catch { /* noop */ }
  };

  // Gesprächsdauer
  useEffect(() => {
    if (!joined) return;
    const start = Date.now();
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [joined]);

  // Lebenszeichen: sofort + alle 30 s. remotes über Ref, damit der Intervall
  // nicht bei jedem Teilnehmerwechsel neu aufgesetzt wird.
  const remotesRef = useRef(remotes);
  useEffect(() => { remotesRef.current = remotes; }, [remotes]);
  useEffect(() => {
    if (!joined || !onHeartbeat) return;
    const beat = () => onHeartbeat(remotesRef.current.length + 1);
    beat();
    const id = window.setInterval(beat, 30_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined]);

  // Vollbild-Status spiegeln (Esc verlässt es am Browser vorbei)
  useEffect(() => {
    const sync = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  // Zoom zurücksetzen, wenn Pin/Ansicht wechselt
  useEffect(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, [pinned, viewMode]);

  // ---- Video an DOM binden --------------------------------------------------
  const bindTile = useCallback((id: StageId) => (el: HTMLDivElement | null) => {
    if (el) tileEls.current.set(id, el);
    else tileEls.current.delete(id);
  }, []);

  /** Lokales Bild (Kamera oder Screen) in seinen aktuellen Container spielen. */
  const playLocal = useCallback(() => {
    const el = tileEls.current.get('local');
    const track = screenRef.current || (camOn ? camRef.current : null);
    if (el && track) track.play(el, { fit: screenRef.current ? 'contain' : 'cover' });
  }, [camOn]);

  useEffect(() => { playLocal(); });
  useEffect(() => {
    for (const user of remotes) {
      const el = tileEls.current.get(String(user.uid));
      if (el && user.videoTrack) user.videoTrack.play(el, { fit: stage === String(user.uid) ? 'contain' : 'cover' });
    }
  });

  // ---- Aktionen -------------------------------------------------------------
  const toggleMute = () => {
    const mic = micRef.current;
    if (!mic) return;
    void mic.setEnabled(muted);
    setMuted(!muted);
  };

  const toggleCam = () => {
    const cam = camRef.current;
    if (!cam || sharing) return;
    void cam.setEnabled(!camOn);
    setCamOn(!camOn);
  };

  const toggleShare = useCallback(async () => {
    const client = clientRef.current;
    if (!client) return;
    if (sharing) {
      const screen = screenRef.current;
      screenRef.current = null;
      if (screen) { await client.unpublish(screen).catch(() => {}); screen.close(); }
      if (camRef.current && camOn) await client.publish(camRef.current).catch(() => {});
      setSharing(false);
      setPinned((p) => (p === 'local' ? null : p));
      return;
    }
    try {
      const { default: AgoraRTC } = await import('agora-rtc-sdk-ng');
      const screen = await AgoraRTC.createScreenVideoTrack({}, 'disable');
      const track = Array.isArray(screen) ? screen[0] : screen;
      screenRef.current = track;
      if (camRef.current) await client.unpublish(camRef.current).catch(() => {});
      await client.publish(track);
      setSharing(true);
      setPinned('local');
      track.on('track-ended', () => { void toggleShare(); });
    } catch {
      toast.error(t('shell:meet.shareFailed', 'Bildschirmfreigabe abgebrochen'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharing, camOn, t]);

  const sendInvites = async () => {
    if (!onInvite || inviting) return;
    const emails = parseEmails(inviteText);
    if (!emails.length) return;
    setInviting(true);
    try {
      const sent = await onInvite(emails);
      toast.success(t('shell:meet.invitesSent', '{{count}} Einladung(en) gesendet', { count: sent }));
      setInviteText('');
      setInvitePanel(false);
    } catch {
      toast.error(t('shell:meet.inviteFailed', 'Einladung fehlgeschlagen'));
    } finally {
      setInviting(false);
    }
  };

  // CC-Panel öffnen: aktuellen Serverzustand laden (nur Steuerberechtigte).
  const openCcPanel = () => {
    setCcPanel((v) => !v);
    if (!ccPanel && captionsControl && !ccState) {
      captionsControl.get().then((st) => {
        setCcState(st);
        if (st.source) setSrcLang(st.source);
        if (st.targets[0]) { setTgtLang(st.targets[0]); setDisplayLang(st.targets[0]); }
      }).catch(() => {});
    }
  };

  const applyCaptions = async () => {
    if (!captionsControl || ccBusy) return;
    setCcBusy(true);
    try {
      const targets = tgtLang !== 'none' ? [tgtLang] : [];
      const st = await captionsControl.start(srcLang, targets);
      setCcState(st);
      setDisplayLang(targets[0] || 'original');
      setCcVisible(true);
      toast.success(t('shell:meet.ccStarted', 'Untertitel laufen'));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('shell:meet.ccFailed', 'Untertitel konnten nicht gestartet werden'));
    } finally {
      setCcBusy(false);
    }
  };

  const stopCaptions = async () => {
    if (!captionsControl || ccBusy) return;
    setCcBusy(true);
    try {
      const st = await captionsControl.stop();
      setCcState(st);
      setCaptionLines({});
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen'));
    } finally {
      setCcBusy(false);
    }
  };

  /** Sprachcode menschenlesbar (Gerätesprache), Fallback: der Code selbst. */
  const langLabel = (code: string) => {
    try { return new Intl.DisplayNames([navigator.language], { type: 'language' }).of(code) || code; }
    catch { return code; }
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void rootRef.current?.requestFullscreen();
  };

  const zoomTo = (next: number) => {
    const clamped = Math.min(3, Math.max(1, next));
    setZoom(clamped);
    if (clamped === 1) setPan({ x: 0, y: 0 });
  };

  const onStagePointerDown = (e: React.PointerEvent) => {
    if (zoom === 1) return;
    dragRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onStagePointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    setPan({ x: d.px + (e.clientX - d.x), y: d.py + (e.clientY - d.y) });
  };
  const onStagePointerUp = () => { dragRef.current = null; };

  // ---- Render ---------------------------------------------------------------
  if (error) {
    return (
      <div className="meet-room flex h-full flex-col items-center justify-center gap-4 p-8">
        <p className="max-w-sm text-center text-sm" style={{ color: 'var(--meet-danger)' }}>{error}</p>
        <button type="button" className="meet-btn" onClick={onLeave}>{t('shell:meet.back', 'Zurück')}</button>
      </div>
    );
  }

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // Untertitel: letzte 2 aktive Zeilen (der Sekundentakt von `elapsed` sorgt
  // dafür, dass abgelaufene Zeilen ohne eigenen Timer verschwinden).
  const nowTs = Date.now();
  const visibleCaptions = ccVisible
    ? Object.entries(captionLines)
        .filter(([, l]) => nowTs - l.ts < 7000 && (l.text || Object.keys(l.trans).length))
        .sort((a, b) => a[1].ts - b[1].ts)
        .slice(-2)
    : [];
  const displayLangs = [...new Set([
    ...(ccState?.targets || []),
    ...Object.values(captionLines).flatMap((l) => Object.keys(l.trans)),
  ])];
  const captionSpeaker = (key: string) =>
    key === String(rtc.uid) ? displayName : (peerNames[key] || `#${key.slice(-4)}`);
  const captionText = (l: CaptionLine) =>
    (displayLang !== 'original' && l.trans[displayLang]) || l.text;
  // Sprecher-Bühne: aktueller Sprecher, sonst erstes Video, sonst erster
  // Teilnehmer, sonst das eigene Bild.
  const speakerStage: StageId = (() => {
    if (speakerUid && remotes.some((u) => String(u.uid) === speakerUid)) return speakerUid;
    const withVideo = remotes.find((u) => u.videoTrack);
    if (withVideo) return String(withVideo.uid);
    if (remotes[0]) return String(remotes[0].uid);
    return 'local';
  })();
  /** Effektive Bühne: Pin > Sprecheransicht; null = Rasteransicht. */
  const stage: StageId | null = pinned ?? (viewMode === 'speaker' ? speakerStage : null);
  const gridActive = stage === null;
  const gridCols = strip_len_cols(remotes.length + 1);
  const stageIsLocal = stage === 'local';
  const stageUser = stageIsLocal ? null : remotes.find((u) => String(u.uid) === stage) || null;
  const stageHasVideo = stageIsLocal ? (sharing || (camOn && hasCam)) : Boolean(stageUser?.videoTrack);
  const strip: { id: StageId; label: string; hasVideo: boolean; muted?: boolean }[] = [
    { id: 'local', label: viewOnly ? `${displayName} 👁` : displayName, hasVideo: sharing || (camOn && hasCam), muted },
    ...remotes.map((u) => ({
      id: String(u.uid),
      label: peerNames[String(u.uid)] || `#${String(u.uid).slice(-4)}`,
      hasVideo: Boolean(u.videoTrack),
    })),
  ];

  return (
    <div ref={rootRef} className="meet-room flex h-full min-h-0 flex-col">
      {/* Kopfzeile — schmal, Eyebrow-Stil wie KI-Entwürfe */}
      <div className="meet-room-head">
        <div className="min-w-0">
          <div className="meet-eyebrow">{t('shell:meet.liveEyebrow', 'Live')}</div>
          <p className="truncate text-sm font-semibold">{title || t('shell:meet.defaultTitle', 'Video-Gespräch')}</p>
        </div>
        <div className="meet-head-chips">
          <span className="meet-chip tabular-nums">{fmt(elapsed)}</span>
          <span className="meet-chip tabular-nums">{remotes.length + 1} {t('shell:meet.participantsShort', 'Teiln.')}</span>
          {viewOnly && <span className="meet-chip is-viewer">{t('shell:meet.viewerBadge', 'Nur zusehen')}</span>}
        </div>
      </div>

      {/* Beitrittsanfragen — Meet tarzı: sahibi call içinde onaylar */}
      {joinReqs.length > 0 && (
        <div className="meet-join-requests">
          {joinReqs.map((r) => (
            <div key={r.id} className="meet-join-request">
              <span className="min-w-0 flex-1 truncate text-xs font-semibold">
                {t('shell:meet.wantsToJoin', '{{name}} möchte beitreten', { name: r.name || '—' })}
              </span>
              <button type="button" className="meet-join-allow" onClick={() => void decideJoin(r.id, 'approve')}>
                {t('shell:meet.allow', 'Zulassen')}
              </button>
              <button type="button" className="meet-join-deny" onClick={() => void decideJoin(r.id, 'deny')}>
                {t('shell:meet.deny', 'Ablehnen')}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Rasteransicht — alle Teilnehmer gleichzeitig (Standard-Alternative) */}
      {gridActive && (
        <div className="meet-grid-wrap">
          <div className="meet-grid" style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}>
            {strip.map((tile) => (
              <button
                key={tile.id}
                type="button"
                onClick={() => setPinned(tile.id)}
                className={cn('meet-grid-tile', tile.id !== 'local' && tile.id === speakerUid && 'is-speaking')}
                title={t('shell:meet.pinTile', 'Anheften: {{name}}', { name: tile.label })}
              >
                <div ref={bindTile(tile.id)} className="h-full w-full" />
                {!tile.hasVideo && (
                  <span className="meet-avatar-overlay"><span className="meet-avatar">{tile.label.slice(0, 1).toUpperCase()}</span></span>
                )}
                <span className="meet-strip-name">{tile.label}{tile.muted ? ' 🔇' : ''}</span>
              </button>
            ))}
          </div>
          {visibleCaptions.length > 0 && (
            <div className="meet-captions">
              {visibleCaptions.map(([key, line]) => (
                <p key={key} className={cn('meet-caption-line', !line.final && 'is-partial')}>
                  <span className="meet-caption-name">{captionSpeaker(key)}</span>
                  {captionText(line)}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bühne */}
      {!gridActive && stage !== null && (
      <div className="meet-stage-wrap">
        <div
          className={cn('meet-stage', zoom > 1 && 'is-zoomed')}
          onDoubleClick={() => zoomTo(zoom > 1 ? 1 : 2)}
          onPointerDown={onStagePointerDown}
          onPointerMove={onStagePointerMove}
          onPointerUp={onStagePointerUp}
        >
          <div
            className="h-full w-full"
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: 'center' }}
          >
            <div ref={bindTile(stage)} className="h-full w-full" />
          </div>
          {!stageHasVideo && (
            <div className="meet-avatar-overlay">
              <span className="meet-avatar">
                {(stageIsLocal ? displayName : `#${stage.slice(-2)}`).slice(0, 1).toUpperCase()}
              </span>
            </div>
          )}
          <span className="meet-stage-name">{stageIsLocal ? displayName : `#${stage.slice(-4)}`}</span>

          {/* Bühnenwerkzeuge: Zoom + Vollbild */}
          <div className="meet-stage-tools">
            <button type="button" className="meet-tool" onClick={() => zoomTo(zoom - 0.5)} disabled={zoom <= 1} title={t('shell:meet.zoomOut', 'Verkleinern')}>
              <ZoomOut className="h-3.5 w-3.5" />
            </button>
            <span className="meet-tool-zoom tabular-nums">{zoom.toFixed(1)}×</span>
            <button type="button" className="meet-tool" onClick={() => zoomTo(zoom + 0.5)} disabled={zoom >= 3} title={t('shell:meet.zoomIn', 'Vergrößern')}>
              <ZoomIn className="h-3.5 w-3.5" />
            </button>
            {pinned && (
              <button type="button" className="meet-tool" onClick={() => setPinned(null)} title={t('shell:meet.unpin', 'Lösen — zurück zur automatischen Ansicht')}>
                <PinOff className="h-3.5 w-3.5" />
              </button>
            )}
            <button type="button" className="meet-tool" onClick={toggleFullscreen} title={t('shell:meet.fullscreen', 'Vollbild')}>
              {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </button>
          </div>

          {/* Live-Untertitel — unten mittig über der Bühne */}
          {visibleCaptions.length > 0 && (
            <div className="meet-captions">
              {visibleCaptions.map(([key, line]) => (
                <p key={key} className={cn('meet-caption-line', !line.final && 'is-partial')}>
                  <span className="meet-caption-name">{captionSpeaker(key)}</span>
                  {captionText(line)}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Filmstreifen — Kachel antippen = anheften (Pin) */}
      {!gridActive && strip.length > 1 && (
        <div className="meet-strip">
          {strip.map((tile) => (
            <button
              key={tile.id}
              type="button"
              onClick={() => setPinned((p) => (p === tile.id ? null : tile.id))}
              className={cn('meet-strip-tile', stage === tile.id && 'is-active')}
              title={tile.label}
            >
              {tile.id !== stage && <div ref={bindTile(tile.id)} className="h-full w-full" />}
              {(!tile.hasVideo || tile.id === stage) && (
                <span className="meet-strip-avatar">{tile.label.slice(0, 1).toUpperCase()}</span>
              )}
              <span className="meet-strip-name">{tile.label}{tile.muted ? ' 🔇' : ''}</span>
            </button>
          ))}
        </div>
      )}

      {/* Einladen — kompaktes Panel über den Kontrollen */}
      {invitePanel && onInvite && (
        <div className="meet-invite-panel">
          <input
            autoFocus
            value={inviteText}
            onChange={(e) => setInviteText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void sendInvites(); }}
            placeholder={t('shell:meet.multiEmailPlaceholder', 'kunde@example.com, partner@firma.de …')}
          />
          <button type="button" className="meet-invite-send" onClick={() => void sendInvites()} disabled={inviting || parseEmails(inviteText).length === 0}>
            {t('shell:meet.sendInvite', 'Einladung senden')}
          </button>
          <button type="button" className="meet-invite-close" onClick={() => setInvitePanel(false)} title={t('shell:meet.back', 'Zurück')}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Untertitel — Sprachwahl & Start/Stopp (Steuerung nur Desk-Nutzer) */}
      {ccPanel && (
        <div className="meet-cc-panel">
          {captionsControl && (
            <>
              <label className="meet-cc-field">
                <span>{t('shell:meet.ccSource', 'Gesprochen')}</span>
                <select value={srcLang} onChange={(e) => setSrcLang(e.target.value)}>
                  {(ccState?.languages || [srcLang]).map((l) => (
                    <option key={l} value={l}>{langLabel(l)}</option>
                  ))}
                </select>
              </label>
              <label className="meet-cc-field">
                <span>{t('shell:meet.ccTarget', 'Übersetzen nach')}</span>
                <select value={tgtLang} onChange={(e) => setTgtLang(e.target.value)}>
                  <option value="none">{t('shell:meet.ccNoTranslate', 'Keine Übersetzung')}</option>
                  {(ccState?.languages || []).filter((l) => l !== srcLang).map((l) => (
                    <option key={l} value={l}>{langLabel(l)}</option>
                  ))}
                </select>
              </label>
              <button type="button" className="meet-cc-apply" onClick={() => void applyCaptions()} disabled={ccBusy}>
                {ccBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                {ccState?.active
                  ? t('shell:meet.ccApply', 'Sprachen übernehmen')
                  : t('shell:meet.ccStart', 'Untertitel starten')}
              </button>
              {ccState?.active && (
                <button type="button" className="meet-cc-stop" onClick={() => void stopCaptions()} disabled={ccBusy}>
                  {t('shell:meet.ccStop', 'Stoppen')}
                </button>
              )}
            </>
          )}
          {(captionsSeen || ccState?.active) && (
            <label className="meet-cc-field">
              <span>{t('shell:meet.ccDisplay', 'Anzeigen als')}</span>
              <select value={displayLang} onChange={(e) => setDisplayLang(e.target.value)}>
                <option value="original">{t('shell:meet.ccOriginal', 'Originalsprache')}</option>
                {displayLangs.map((l) => (
                  <option key={l} value={l}>{langLabel(l)}</option>
                ))}
              </select>
            </label>
          )}
          {(captionsSeen || ccState?.active) && (
            <button type="button" className="meet-cc-stop" onClick={() => setCcVisible((v) => !v)}>
              {ccVisible ? t('shell:meet.ccHide', 'Ausblenden') : t('shell:meet.ccShow', 'Einblenden')}
            </button>
          )}
          {!captionsControl && !captionsSeen && (
            <span className="meet-cc-hint">{t('shell:meet.ccGuestHint', 'Untertitel startet das Support-Team.')}</span>
          )}
          <button type="button" className="meet-invite-close" onClick={() => setCcPanel(false)} title={t('shell:meet.back', 'Zurück')}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Kontrollleiste — mobil: fest unten mit Safe-Area */}
      <div className="meet-controls">
        {!viewOnly && (
          <>
            <button type="button" className={cn('meet-ctl', muted && 'is-danger')} onClick={toggleMute} disabled={!joined} title={muted ? t('shell:voice.unmute', 'Stummschaltung aufheben') : t('shell:voice.mute', 'Stummschalten')}>
              {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
            <button type="button" className={cn('meet-ctl', (!camOn || !hasCam) && 'is-danger')} onClick={toggleCam} disabled={!joined || !hasCam || sharing} title={t('shell:meet.camera', 'Kamera')}>
              {camOn && hasCam ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
            </button>
            <button type="button" className={cn('meet-ctl', sharing && 'is-live')} onClick={() => void toggleShare()} disabled={!joined} title={t('shell:meet.share', 'Bildschirm teilen')}>
              <MonitorUp className="h-5 w-5" />
            </button>
          </>
        )}
        <button
          type="button"
          className={cn('meet-ctl', gridActive && 'is-live')}
          onClick={() => { setPinned(null); setViewMode((v) => (v === 'grid' ? 'speaker' : 'grid')); }}
          disabled={!joined}
          title={gridActive
            ? t('shell:meet.viewSpeaker', 'Ansicht: Aktiver Sprecher')
            : t('shell:meet.viewGrid', 'Ansicht: Raster — alle gleichzeitig')}
        >
          <LayoutGrid className="h-5 w-5" />
        </button>
        {(captionsControl || captionsSeen) && (
          <button
            type="button"
            className={cn('meet-ctl', (ccPanel || ccState?.active) && 'is-live')}
            onClick={openCcPanel}
            title={t('shell:meet.ccTitle', 'Live-Untertitel')}
          >
            <Captions className="h-5 w-5" />
          </button>
        )}
        {onInvite && (
          <button type="button" className={cn('meet-ctl', invitePanel && 'is-live')} onClick={() => setInvitePanel((v) => !v)} title={t('shell:meet.invite', 'Per E-Mail einladen')}>
            <UserPlus className="h-5 w-5" />
          </button>
        )}
        <button type="button" className="meet-ctl is-leave" onClick={onLeave} title={t('shell:meet.leave', 'Verlassen')}>
          <PhoneOff className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
